import { useNavigate } from 'react-router-dom'
import { useTranslation } from '../../i18n'
import { workoutApi } from '../../api/workoutApi'
import type { WorkoutSession } from '../../api/workoutApi'

interface ActiveWorkoutWidgetProps {
  workout: WorkoutSession
}

/** Resume card for an in-progress workout — home hero for solo, trainee, and coach. */
export default function ActiveWorkoutWidget({ workout }: ActiveWorkoutWidgetProps) {
  const navigate = useNavigate()
  const { t } = useTranslation()

  async function openSession() {
    try {
      const active = await workoutApi.getActiveSession()
      if (active?.id != null) {
        navigate(`/training/session/${active.id}`)
        return
      }
    } catch {
      // Fall through to the known session id
    }
    navigate(`/training/session/${workout.id}`)
  }

  return (
    <div
      data-tour="tour-trainee-get-started"
      className="mb-5 rounded-2xl p-4 cursor-pointer active:scale-[0.98] transition-transform relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 55%, #2dd4bf 100%)',
        boxShadow: '0 8px 28px rgba(13, 148, 136, 0.28)',
      }}
      onClick={() => void openSession()}
    >
      <div
        className="absolute pointer-events-none"
        style={{
          width: 160,
          height: 160,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.06)',
          top: -40,
          right: -40,
        }}
      />
      <div
        className="absolute pointer-events-none"
        style={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.05)',
          bottom: -20,
          right: 60,
        }}
      />
      <div className="relative">
        <div className="flex items-center justify-between mb-3">
          <span
            className="text-xs font-semibold tracking-widest uppercase"
            style={{ color: 'rgba(255,255,255,0.7)' }}
          >
            {t.workout.resumeTitle}
          </span>
          <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 18 }}>→</span>
        </div>
        <div className="text-lg font-bold mb-1" style={{ color: 'white' }}>
          {workout.blockName || t.workout.sessionTitle}
        </div>
        <p className="text-xs mb-4" style={{ color: 'rgba(255,255,255,0.78)' }}>
          {t.traineeDashboard.activeWorkout.subtitle}
        </p>
        <button
          type="button"
          className="w-full rounded-xl py-2.5 text-sm font-semibold active:scale-[0.98] transition-transform"
          style={{ background: 'rgba(255,255,255,0.95)', color: '#0f766e' }}
          onClick={(e) => {
            e.stopPropagation()
            void openSession()
          }}
        >
          {t.workout.resumeCta}
        </button>
      </div>
    </div>
  )
}
